import { useEffect, useState } from 'react';
import { ApiError, api, download, type DayHours, type Site, type TourSheet } from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';
import { navigate } from '../lib/router';

/**
 * The scheduler's side of timesheets (D31, changed by D35): pick a hospital
 * and a tour (Tuesday to the following Tuesday), see everyone who worked it
 * with the scheduled hours, correct any sheet day by day, see and answer
 * physicians' questions, approve, and download the sheets — one, or the
 * whole packet for the hospital. Physicians only read their sheets.
 */
export function AdminTimesheets({ siteId, periodStart, sites, editedBy }: { siteId: string; periodStart: string; sites: Site[]; editedBy: string }) {
  const [rows, setRows] = useState<TourSheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.tourSheets(siteId, periodStart).then(setRows).catch((e: Error) => setError(e.message));
  };
  useEffect(load, [siteId, periodStart]);

  const go = (s: string, p: string) => navigate(`/admin/timesheets?siteId=${s}&periodStart=${p}`);
  const approve = async (abbreviation: string) => {
    setBusy(abbreviation);
    setProblem(null);
    try {
      await api.approveSheet(siteId, periodStart, abbreviation, editedBy || 'unknown');
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const get = async (path: string, name: string) => {
    setProblem(null);
    try {
      await download(path, name);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const resolve = async (abbreviation: string) => {
    setBusy(abbreviation);
    setProblem(null);
    try {
      await api.resolveHoursQuestion(abbreviation, siteId, periodStart, editedBy || 'unknown');
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const siteName = sites.find((s) => s.id === siteId)?.name ?? siteId;
  return (
    <section className="timesheets">
      <div className="gridtools">
        <label className="field inline">
          <span className="muted">Hospital</span>
          <select value={siteId} onChange={(e) => go(e.target.value, periodStart)}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="quarterpicker">
          <button type="button" onClick={() => go(siteId, addDays(periodStart, -7))} aria-label="Previous tour">‹</button>
          <h1>Tour of {dayLabel(periodStart).replace(/^\w+, /, '')} – {dayLabel(addDays(periodStart, 7)).replace(/^\w+, /, '')}</h1>
          <button type="button" onClick={() => go(siteId, addDays(periodStart, 7))} aria-label="Next tour">›</button>
        </div>
        {rows && rows.length > 0 && (
          <button className="primary" type="button" onClick={() => get(api.packetPath(siteId, periodStart), `${siteId}_${periodStart}_timesheets.zip`)}>
            Download all {rows.length} sheets for {siteName}
          </button>
        )}
        {/* The other half of the same tour (D116, D119): a timesheet reports a week that has
            been worked, a confirmation asks a physician to commit to one that has not. Both
            are the same form, so they belong at the same place — this page already knows the
            hospital and the tour, and the office is already here.

            Shown whenever there are sheets rather than only for a published week, because
            this page cannot tell: forTour returns rows for finalized weeks too, and the SHEET
            status it does have is a different thing. The route refuses an unpublished week in
            a sentence that explains why, which teaches the rule better than a missing button. */}
        {rows && rows.length > 0 && (
          <button
            type="button"
            title="One shift confirmation per physician, zipped — for a published tour. Send these for signature."
            onClick={() => get(api.confirmationsPath(siteId, periodStart), `${siteId}_${periodStart}_shift-confirmations.zip`)}
          >
            Shift confirmations ({rows.length})
          </button>
        )}
      </div>
      {error && <p className="error">Couldn't load timesheets ({error}).</p>}
      {problem && <p className="error">{problem}</p>}
      {!rows && !error && <p className="muted">Loading…</p>}
      {rows && rows.length === 0 && <p className="muted">Nobody has published shifts at {siteName} in this tour.</p>}
      {rows && rows.length > 0 && (
        <div className="gridscroll">
          <table className="grid sheets">
            <thead>
              <tr>
                <th className="sticky name">Physician</th>
                <th>Scheduled</th>
                <th>Recorded</th>
                <th>Status</th>
                <th>From the physician</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <>
                  <tr key={r.physician.abbreviation} className={`st-${r.status}`}>
                    <th className="sticky name">
                      <button className="linklike" type="button" onClick={() => setOpen(open === r.physician.abbreviation ? null : r.physician.abbreviation)}>{r.physician.lastName}, {r.physician.firstName}</button>
                    </th>
                    <td className="num">{r.totals.scheduled} h</td>
                    <td className="num">{r.totals.recorded == null ? <span className="muted">—</span> : <span className={r.totals.recorded !== r.totals.scheduled ? 'warn' : ''}>{r.totals.recorded} h</span>}</td>
                    <td>
                      <span className={`pill st-${r.status}`}>{r.status === 'scheduled' ? 'as scheduled' : r.status === 'recorded' ? `corrected by ${r.recordedBy}` : `approved by ${r.approvedBy}`}</span>
                      {/* The physician's own confirmation (D57) — who still needs chasing before you sign off. */}
                      {r.physicianApprovedAt ? (
                        <span className="pill st-approved" title={`Confirmed by ${r.physicianApprovedBy}`}>Dr confirmed</span>
                      ) : (
                        <span className="pill st-todo">awaiting Dr</span>
                      )}
                    </td>
                    <td className="notes">
                      {r.question && !r.questionResolvedAt ? (
                        <span className="question"><span className="pill st-question">question</span> {r.question}</span>
                      ) : r.question ? (
                        <span className="muted">answered by {r.questionResolvedBy}: {r.question}</span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="actions">
                      {r.question && !r.questionResolvedAt && (
                        <button type="button" disabled={busy === r.physician.abbreviation} onClick={() => resolve(r.physician.abbreviation)}>Mark answered</button>
                      )}
                      <button type="button" onClick={() => setOpen(open === r.physician.abbreviation ? null : r.physician.abbreviation)}>{open === r.physician.abbreviation ? 'Close' : 'Hours'}</button>
                      {r.status !== 'approved' && (
                        <button type="button" disabled={busy === r.physician.abbreviation} onClick={() => approve(r.physician.abbreviation)}>
                          {r.status === 'scheduled' ? 'Approve as scheduled' : 'Approve'}
                        </button>
                      )}
                      <button type="button" onClick={() => get(api.sheetPath(r.physician.abbreviation, siteId, periodStart), `${r.physician.lastName}_${periodStart}_timesheet.xlsx`)}>Download sheet</button>
                    </td>
                  </tr>
                  {open === r.physician.abbreviation && (
                    <tr key={`${siteId}-${periodStart}-${r.physician.abbreviation}-days`} className="detail">
                      <td colSpan={6}>
                        <RecordForm sheet={r} siteId={siteId} periodStart={periodStart} editedBy={editedBy} onSaved={load} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="legend muted">
        Scheduled = from the published schedule. Recorded = your correction, day by day (open <em>Hours</em>). Physicians see their hours and can send a question from their <em>Timesheets</em> page; it shows here until you mark it answered. Approve locks the sheet with your name; a later correction reopens it.
      </p>
    </section>
  );
}

/**
 * The office's correction of one sheet (D35): the scheduled hours, editable
 * day by day, saved with the scheduler's name and a note. Changed cells are
 * marked; the physician sees the corrected sheet on their page.
 */
function RecordForm({ sheet, siteId, periodStart, editedBy, onSaved }: { sheet: TourSheet; siteId: string; periodStart: string; editedBy: string; onSaved: () => void }) {
  const [days, setDays] = useState<DayHours[]>(() => (sheet.recorded ?? sheet.scheduled).map((d) => ({ ...d })));
  const [note, setNote] = useState(sheet.note ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const dirty = JSON.stringify(days) !== JSON.stringify(sheet.recorded ?? sheet.scheduled) || note !== (sheet.note ?? '');

  const set = (i: number, k: 'day' | 'swing' | 'night', v: string) => {
    const n = v === '' ? 0 : Number(v);
    if (!Number.isFinite(n) || n < 0 || n > 24) return;
    setDays((ds) => ds.map((d, j) => (j === i ? { ...d, [k]: Math.round(n * 4) / 4 } : d)));
    setSaved(false);
  };
  const setNoteFor = (i: number, v: string) => {
    setDays((ds) => ds.map((d, j) => (j === i ? { ...d, note: v } : d)));
    setSaved(false);
  };
  const total = days.reduce((n, d) => n + d.day + d.swing + d.night, 0);
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.recordHours(sheet.physician.abbreviation, siteId, periodStart, { days: days.map((d) => ({ ...d, note: d.note || '' })), note: note.trim() || null, editedBy: editedBy || null });
      setSaved(true);
      onSaved();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="confirm">
      <table className="hours edit">
        <thead>
          <tr>
            <th>Day</th>
            <th>Day<br /><span className="muted">7a–7p</span></th>
            <th>Swing<br /><span className="muted">7p–11p</span></th>
            <th>Night<br /><span className="muted">11p–7a</span></th>
            <th>Explanation</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d, i) => (
            <tr key={d.date}>
              <td className="daycell">{dayLabel(d.date)}</td>
              {(['day', 'swing', 'night'] as const).map((k) => (
                <td key={k} className="num">
                  <input type="number" inputMode="decimal" min={0} max={24} step={0.25} value={d[k] || ''} placeholder="0" onChange={(e) => set(i, k, e.target.value)} className={sheet.scheduled[i][k] !== d[k] ? 'changed' : ''} title={sheet.scheduled[i][k] !== d[k] ? `schedule: ${sheet.scheduled[i][k]}` : undefined} />
                </td>
              ))}
              <td className="notes"><input type="text" value={d.note} maxLength={300} onChange={(e) => setNoteFor(i, e.target.value)} placeholder="e.g. Day hours 7a-7p" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="confirm-foot">
        <span><strong>{total} h</strong> total{total !== sheet.totals.scheduled && <span className="muted"> (schedule said {sheet.totals.scheduled} h)</span>}</span>
        <label className="field grow">
          <span>Note for the hospital's sheet</span>
          <input type="text" value={note} maxLength={1000} onChange={(e) => { setNote(e.target.value); setSaved(false); }} placeholder="e.g. Stayed for backup Saturday night (per Dr. X)" />
        </label>
        {problem && <p className="error">{problem}</p>}
        <div className="formactions">
          <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save correction'}</button>
          {saved && <span className="ok small">Saved as {editedBy || 'unknown'} — approve when ready.</span>}
          {sheet.status === 'approved' && dirty && <span className="muted small">Saving reopens the approval.</span>}
        </div>
      </div>
    </div>
  );
}

/** Scheduled beside recorded, day by day, with the differences marked. */
export function HoursTable({ sheet }: { sheet: TourSheet }) {
  const conf = sheet.recorded;
  return (
    <table className="hours">
      <thead>
        <tr>
          <th>Day</th>
          <th>Day 7a–7p</th>
          <th>Swing 7p–11p</th>
          <th>Night 11p–7a</th>
          <th>Total</th>
          <th>Explanation</th>
        </tr>
      </thead>
      <tbody>
        {sheet.scheduled.map((d, i) => {
          const c = conf?.[i];
          const cell = (k: 'day' | 'swing' | 'night') =>
            c && c[k] !== d[k] ? <span><s className="muted">{d[k] || '·'}</s> <strong className="warn">{c[k]}</strong></span> : <span>{d[k] || <span className="muted">·</span>}</span>;
          const total = (x: { day: number; swing: number; night: number }) => x.day + x.swing + x.night;
          return (
            <tr key={d.date}>
              <td>{dayLabel(d.date)}</td>
              <td className="num">{cell('day')}</td>
              <td className="num">{cell('swing')}</td>
              <td className="num">{cell('night')}</td>
              <td className="num">{c && total(c) !== total(d) ? <span><s className="muted">{total(d)}</s> <strong className="warn">{total(c)}</strong></span> : total(d) || <span className="muted">·</span>}</td>
              <td className="notes">{c?.note && c.note !== d.note ? <span><s className="muted">{d.note}</s> {c.note}</span> : d.note}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
